import { normalizeComplianceStandardCode } from './complianceStandard';
import { getCanonicalStandardCode, isSameStandard } from './standardNormalizer';

export const NIS2_LEGACY_ALIAS_CODES = new Set(['NIS2_FULL', 'NIS2_FULL_2024']);

function normalizeRaw(code: string | null | undefined): string {
  return String(code ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

export function isNis2LegacyAliasCode(code: string | null | undefined): boolean {
  return NIS2_LEGACY_ALIAS_CODES.has(normalizeRaw(code));
}

export function isHiddenFromUxStandardCode(code: string | null | undefined): boolean {
  return isNis2LegacyAliasCode(code);
}

export function toUserFacingStandardCode(code: string | null | undefined): string {
  const canon = getCanonicalStandardCode(code) || normalizeComplianceStandardCode(code);
  if (canon === 'ALL') return '';
  return canon;
}

export function isNis2CanonicalStandard(code: string | null | undefined): boolean {
  return toUserFacingStandardCode(code) === 'NIS2';
}

export type StandardUxItem = {
  code?: string;
  standardCode?: string;
  value?: string;
  name?: string;
  label?: string;
  active?: boolean;
  enabled?: boolean;
  description?: string | null;
};

export function collapseStandardsForUx(
  items: StandardUxItem[],
  options: { mergeActive?: boolean; nis2Label?: string } = {},
): StandardUxItem[] {
  const { mergeActive = true, nis2Label = 'NIS2' } = options;
  const byCanon = new Map<string, StandardUxItem>();

  for (const item of items) {
    const rawCode = item.code ?? item.standardCode ?? item.value;
    if (!rawCode) continue;
    const canon = toUserFacingStandardCode(rawCode);
    if (!canon) continue;

    const existing = byCanon.get(canon);
    const displayName =
      canon === 'NIS2' ? nis2Label : item.name || item.label || existing?.name || existing?.label || canon;

    const next: StandardUxItem = {
      ...existing,
      ...item,
      code: canon,
      standardCode: canon,
      name: displayName,
      label: canon === 'NIS2' ? nis2Label : item.label ?? displayName,
    };

    if (mergeActive) {
      next.active = Boolean(existing?.active || item.active || item.enabled);
      next.enabled = Boolean(existing?.enabled || item.enabled || item.active);
    }

    byCanon.set(canon, next);
  }

  return Array.from(byCanon.values()).sort((a, b) =>
    String(a.name || a.code).localeCompare(String(b.name || b.code)),
  );
}

export function normalizeStarterRelevantStandards(standards: string[] | null | undefined): string[] {
  const out = new Set<string>();
  for (const raw of standards || []) {
    const s = String(raw || '').trim();
    if (!s) continue;
    const canon = normalizeComplianceStandardCode(s);
    if (!canon || canon === 'ALL') continue;
    if (canon === 'NIS2') {
      out.add('NIS2');
      continue;
    }
    if (canon === 'CIS') {
      out.add('CIS Controls v8');
      continue;
    }
    if (canon === 'SOC2') {
      out.add('SOC 2');
      continue;
    }
    if (canon === 'ISO27001') {
      out.add('ISO/IEC 27001');
      continue;
    }
    if (canon === 'ISO42001') {
      out.add('ISO/IEC 42001:2023');
      continue;
    }
    if (canon === 'PCI_DSS_2025') {
      out.add('PCI DSS 2025');
      continue;
    }
    if (canon === 'SWIFT_CSCF_2025') {
      out.add('SWIFT CSCF 2025');
      continue;
    }
    if (canon === 'SDLC_SECURITY') {
      out.add('SDLC Security');
      continue;
    }
    if (canon === 'TECH_CONTROLS' || canon === 'TECHNICAL_CONTROLS') {
      out.add('Technical Controls');
      continue;
    }
    if (canon === 'ENS_ANSSI') {
      out.add('ANSSI_FR');
      continue;
    }
    if (canon === 'ENS_ES') {
      out.add('ENS_ES');
      continue;
    }
    out.add(s);
  }
  return Array.from(out);
}

export function standardsEquivalentForUx(left: unknown, right: unknown): boolean {
  return isSameStandard(String(left ?? ''), String(right ?? ''));
}
